import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import Textarea from "@/components/ui/Textarea";

interface CancelBookingModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  loading?: boolean;
}

export default function CancelBookingModal({
  open,
  onClose,
  onConfirm,
  loading,
}: CancelBookingModalProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError("Please provide a reason for cancellation.");
      return;
    }
    setError("");
    await onConfirm(reason.trim());
  };

  return (
    <Modal open={open} onClose={onClose} title="Cancel Booking" size="sm">
      <div className="space-y-4">
        {/* Warning */}
        <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-4">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-500" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Are you sure?</p>
            <p>
              Cancellations are subject to our cancellation policy. A refund
              will be processed by our team within 5–7 business days if
              eligible.
            </p>
          </div>
        </div>

        <Textarea
          label="Reason for cancellation"
          required
          rows={3}
          placeholder="Please let us know why you're cancelling…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={error}
        />

        <div className="flex gap-3 border-t border-gray-100 pt-4">
          <Button
            variant="outline"
            fullWidth
            onClick={onClose}
            disabled={loading}
          >
            Keep Booking
          </Button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 whitespace-nowrap rounded-full bg-red-500 px-4 py-3 text-sm font-medium text-white transition hover:bg-red-600 disabled:opacity-60"
          >
            {loading ? "Cancelling…" : "Cancel Booking"}
          </button>
        </div>
      </div>
    </Modal>
  );
}